import React, { useEffect, useId, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Worker do pdf.js empacotado pelo Vite (react-pdf 7 / pdf.js 3 — compatível com Safari iOS mais antigo)
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

// PDF copiado para public/ porque o site da AFMM não envia CORS (pdf.js não consegue buscá-lo direto)
const URL_TERMO = '/termos/TERMO-DE-RESPONSABILIDADE.pdf';

// Padding horizontal da área do PDF (p-2 = 8px de cada lado)
const PADDING_PDF = 16;

interface ITermoUsoProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
}

export default function TermoUso({ title, message, onConfirm, onCancel, onClose }: ITermoUsoProps) {
  const [aceito, setAceito] = useState(false);
  const [numPaginas, setNumPaginas] = useState(0);
  const [largura, setLargura] = useState(0);
  const containerPdfRef = useRef<HTMLDivElement>(null);
  const tituloId = useId();

  // Ajusta a largura das páginas ao container (mobile x desktop e rotação de tela)
  useEffect(() => {
    const container = containerPdfRef.current;
    if (!container) return;

    const observer = new ResizeObserver(([entry]) => setLargura(entry.contentRect.width));
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const mensagemPdf = (texto: string) => (
    <p className='py-10 text-center text-sm text-zinc-500'>{texto}</p>
  );

  return (
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby={tituloId}
      className='flex max-h-[90dvh] w-[calc(100vw-2rem)] max-w-3xl animate-slide-up flex-col gap-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-card motion-reduce:animate-none md:p-6'
    >
      <div>
        <h1 id={tituloId} className='text-lg font-semibold text-zinc-900'>{title}</h1>
        <p className='mt-1 text-sm text-zinc-500'>{message}</p>
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-2'>
        <div className='flex items-center justify-between gap-2'>
          <span className='text-sm font-medium text-zinc-900'>Termos e Condições de Uso</span>
          <a
            href={URL_TERMO}
            target='_blank'
            rel='noreferrer'
            className='rounded text-xs text-zinc-500 underline hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
          >
            Abrir em nova aba
          </a>
        </div>
        <div
          ref={containerPdfRef}
          className='h-[55dvh] min-h-0 overflow-y-auto rounded-xl border border-zinc-200 bg-zinc-100 p-2 md:h-[60vh]'
        >
          <Document
            file={URL_TERMO}
            onLoadSuccess={({ numPages }) => setNumPaginas(numPages)}
            loading={mensagemPdf('Carregando termo...')}
            error={mensagemPdf('Não foi possível carregar o termo. Use o link "Abrir em nova aba".')}
            className='flex flex-col items-center gap-2'
          >
            {largura > 0 && Array.from({ length: numPaginas }, (_, i) => (
              <Page
                key={i}
                pageNumber={i + 1}
                width={largura - PADDING_PDF}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={mensagemPdf(`Carregando página ${i + 1}...`)}
                className='overflow-hidden rounded-lg shadow-card'
              />
            ))}
          </Document>
        </div>
      </div>

      <label className='flex cursor-pointer items-start gap-3 text-sm text-zinc-900'>
        <input
          type='checkbox'
          checked={aceito}
          onChange={e => setAceito(e.target.checked)}
          className='mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
        />
        Eu estou ciente e estou de acordo com os Termos e Condições de Uso.
      </label>

      <div className='grid grid-cols-2 gap-2 md:flex md:justify-end'>
        <button
          type='button'
          onClick={() => {
            onCancel();
            onClose();
          }}
          className='h-12 rounded-xl border border-zinc-200 px-6 font-semibold text-zinc-900 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
        >
          Cancelar
        </button>
        <button
          type='button'
          disabled={!aceito}
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className='h-12 rounded-xl bg-zinc-900 px-6 font-semibold text-white hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-400'
        >
          Confirmar
        </button>
      </div>
    </div>
  );
}
